import { feedBecause, feedName, formatChange, formatUsd, marketPhrase } from "../format";
import { lineClass, lineStyle } from "../line-style";
import type { SetupProps } from "./types";

/**
 * The original chrome, moved here unchanged.
 *
 * A status bar of numbers, a ticker row beneath it, the scrollback, and the
 * prompt pinned to the bottom. Every class name it uses is the one `styles.css`
 * already declared, so this is a move rather than a rewrite — which is the
 * point: if extracting the seam had changed how the existing look renders, the
 * seam would have been in the wrong place.
 */
export function ClassicChrome({ model }: SetupProps) {
  const { snapshot, marked, session, quotes, lines, busy, link, markets, feed, liveVenue } = model;
  // Deployment-wide rather than per-venue: it comes from one feed policy, so
  // every listed market reports the same number and the first one is it.
  const delayMinutes = markets[0]?.quote_delay_minutes ?? 0;
  // `ADR-0039`: an account priced by its own real-time plan says so, rather
  // than saying nothing and leaving a trader to wonder whether it is delayed.
  const realtime = markets[0]?.realtime === true;

  return (
    <div className="term">
      <header className="term-head">
        <span className="brand">atc</span>
        {snapshot && marked ? (
          <>
            <span className="stat">
              account <b>{session.accountName ?? "account"}</b>
            </span>
            {liveVenue ? (
              <span
                className="stat live"
                title="every order on this account is placed on your live Alpaca account — real money"
              >
                LIVE <b>{liveVenue.accountNumber ?? "alpaca"}</b>
              </span>
            ) : null}
            <span className="stat">
              equity <b>{formatUsd(marked.equity)}</b>
            </span>
            <span className="stat">
              cash <b>{formatUsd(snapshot.cash)}</b>
            </span>
            {/*
              The third number is what makes the first two check out. Equity
              minus cash is the market value of the holdings, and without it on
              screen a trader holding 76,000 of stock sees a 76,000 gap with
              nothing to explain it — which reads as a wrong number rather than
              a missing one. Hidden when flat, where it would only say 0.00.
            */}
            {marked.marketValue !== "0.0000" ? (
              <span className="stat">
                holdings <b>{formatUsd(marked.marketValue)}</b>
              </span>
            ) : null}
            {/*
              Cash that a resting order is holding. Surfaced only when it is
              non-zero, because "you have 100,000 and can spend 2,000" is
              precisely the thing a trader must not have to infer.
            */}
            {snapshot.reserved_cash !== "0.0000" ? (
              <span className="stat dim">
                reserved <b>{formatUsd(snapshot.reserved_cash)}</b>
              </span>
            ) : null}
          </>
        ) : (
          <span className="stat dim">{session.email ? "no account yet" : "not signed in"}</span>
        )}
        {/*
          Whether the venue is trading, and when that next changes. It sits with
          equity and cash rather than on the ticker row because it governs both:
          an order placed while the market is shut is refused, and this is the
          only thing on screen that says so before the refusal does.

          Times are the venue's own, not the reader's — a US session rendered in
          Cairo used to read "opens 04:30 PM" (M4/E3).
        */}
        {markets.map((m) => (
          <span
            className={`stat market market-${m.state}`}
            key={m.id}
            title={`${m.name} · ${m.time_zone}`}
          >
            {m.id} <b>{marketPhrase(m)}</b>
          </span>
        ))}
        {/*
          How far behind these prices are. It was knowable only by comparing a
          quote's `as_of` against your own clock, which is a thing nobody does —
          so a fifteen-minute-old number sat next to a live-looking ticker with
          nothing to say it was not the market. The figure is the server's, from
          the same policy that decides whether a resting order may trigger, so
          it cannot drift from what the feed is actually doing.
        */}
        {realtime ? (
          <span className="stat" title="priced in real time by your own data plan">
            <b>real-time</b>
          </span>
        ) : delayMinutes > 0 ? (
          <span className="stat dim" title="the price feed is behind the market by this much">
            delayed <b>{delayMinutes}m</b>
          </span>
        ) : null}

        {/*
          WHOSE feed is pricing you, which the header never said (M4/E53). A
          venue is not an answer: `US` meant a trader's own key was working and
          `SIM` meant it had stopped, and nobody was ever taught that mapping —
          so a trader on the simulator and a trader on their own Alpaca licence
          read an identical line. M4/E52 made the venue REACT to a lapse; this
          is what makes the reaction legible.

          Omitted rather than guessed when there is no answer: `/api/v1/feeds`
          is behind a session, so a guest has none.
        */}
        {feed ? (
          <span className="stat dim" title={`these prices are ${feedBecause(feed.because)}'s`}>
            feed <b>{feedName(feed)}</b> · {feedBecause(feed.because)}
          </span>
        ) : null}

        <span className="spacer" />
        {/*
          Whether the numbers to the left are live. Without it a frozen ticker
          is indistinguishable from a market that is not moving, and the user
          has no way to tell a dead socket from a quiet one.
        */}
        {session.userId ? (
          <span className={`stat link link-${link}`} title={`market data ${link}`}>
            ● {link}
          </span>
        ) : null}
        <span className="stat dim">{session.email ?? "guest"}</span>
      </header>

      <div className="term-ticker">
        {quotes.length === 0 ? (
          <span className="dim">
            {link === "live" ? "waiting for the first tick…" : "connecting to market data…"}
          </span>
        ) : (
          quotes.map((q) => (
            /*
              Two glyphs, two facts. The arrow is the last real price move; the
              colour is whether the venue was open when that price was taken.
              Before M4/I6 the colour tried to carry both and carried neither —
              a frame arrives every second, so an unchanged price greyed the row
              for that second, and a shut market greyed it permanently.

              A shut row keeps its price and loses its arrow: the number is
              still the last thing that traded, and pointing at a direction
              nothing can follow would invite reading it as movement.
            */
            <span
              className={`tick${q.open ? (q.direction ? ` ${q.direction}` : "") : " closed"}`}
              key={q.symbol}
              title={q.open ? undefined : `${q.symbol} — market ${q.state}`}
            >
              {/*
                A shut venue loses its COLOUR, not its arrow. What the stock did
                today is still true after the bell — it is the last word on the
                day, and a row that forgets it at 16:00 is less useful than one
                that keeps it. The colour going grey is what says the number has
                stopped moving.
              */}
              <b>{q.symbol}</b> {formatUsd(q.last)}
              {q.direction === "up" ? " ▲" : q.direction === "down" ? " ▼" : ""}
              {/*
                The number, not only its sign. An arrow says which way; a
                trader's next question is always how far, and making them work
                it out from two prices is the thing a ticker exists to save
                them. Absent when there is no reference — never a made-up 0.00%.
              */}
              {q.changePercent === undefined ? "" : ` ${formatChange(q.changePercent)}`}
            </span>
          ))
        )}
      </div>

      <div className="term-body" ref={model.scrollRef}>
        {lines.map((line, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: scrollback is append-only
          <div key={i} className={lineClass(line)} style={lineStyle(line)}>
            {line.kind === "echo" ? <span className="prompt">&gt; </span> : null}
            {line.text}
          </div>
        ))}
        {busy ? <div className="line dim">…</div> : null}
        <div className="term-input">
          {/*
            The question takes the prompt's place while one is pending, so what
            the terminal wants is where the user is already looking. Without it
            the caret sits after `>` and the ask is three lines up in scrollback.
          */}
          <span className="prompt">{model.pending ? model.pending.label : ">"}</span>
          <input
            ref={model.inputRef}
            value={model.input}
            onChange={(e) => model.setInput(e.target.value)}
            onKeyDown={model.onKeyDown}
            /**
             * M2/E1: NOT `disabled={busy}`.
             *
             * Disabling a focused element blurs it, and re-enabling does not give
             * focus back — so every command that reached the API dropped the
             * caret and the next one had to start with a mouse click. `submit`
             * already refuses to run while busy, so the attribute was buying
             * nothing and costing the terminal its most basic property.
             *
             * `readOnly` would also stop the double-submit, but it would stop the
             * user typing their next command while a fill is in flight, which is
             * worse than the bug.
             */
            aria-busy={busy}
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            aria-label="terminal input"
            // biome-ignore lint/a11y/noAutofocus: it is a terminal; the prompt is the page
            autoFocus
          />
        </div>
      </div>
    </div>
  );
}
